import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { NotebookPen } from "lucide-react";

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/journal").then((r) => setEntries(r.data.entries)); }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-5">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Trade Journal</h1>
        <p className="text-tc-sub mt-1">Every trade is recorded. Learn from repeated patterns, not single outcomes.</p>
      </div>
      {entries.length === 0 ? (
        <div className="tc-card p-10 text-center text-tc-muted">
          <NotebookPen className="w-8 h-8 mx-auto mb-3 opacity-50" />
          No journal entries yet. Complete a trade in Market Replay to start journaling.
          <div className="mt-4">
            <Link to="/replay" data-testid="journal-empty-replay-link" className="inline-flex items-center gap-1.5 rounded-lg bg-tc-coach text-tc-bg font-semibold px-4 py-2 text-sm hover:bg-tc-coach/90">Open Market Replay</Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => {
            const up = (e.pnl ?? 0) >= 0;
            const isOpen = sel === e.id;
            return (
              <div key={e.id} className="tc-card overflow-hidden" data-testid={`journal-entry-${e.trade_id}`}>
                <button onClick={() => setSel(isOpen ? null : e.id)} className="w-full flex items-center justify-between gap-3 p-4 hover:bg-white/5">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-mono px-2 py-1 rounded ${e.direction === "LONG" ? "bg-tc-bull/15 text-tc-bull" : "bg-tc-bear/15 text-tc-bear"}`}>{e.direction}</span>
                    <div className="text-left">
                      <div className="font-medium text-sm">{e.symbol} · {e.interval}</div>
                      <div className="text-[11px] text-tc-muted">{e.headline}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-right">
                    <div><div className="text-[9px] font-mono uppercase text-tc-muted">Score</div><div className="font-mono text-tc-mastery">{e.score}</div></div>
                    <div><div className="text-[9px] font-mono uppercase text-tc-muted">P&L</div><div className={`font-mono ${up ? "text-tc-bull" : "text-tc-bear"}`}>{up ? "+" : ""}${e.pnl}</div></div>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-white/10 p-4 space-y-3 text-sm">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-[13px]">
                      <KV k="Entry" v={e.entry} /><KV k="Stop" v={e.original_stop} /><KV k="Target" v={e.original_target} /><KV k="R:R" v={e.risk_reward} />
                      <KV k="Exit" v={e.exit_reason} /><KV k="R" v={e.r_multiple} /><KV k="Emotion" v={e.emotional_state} /><KV k="Qty" v={e.quantity} />
                    </div>
                    {e.thesis && <div><span className="text-tc-muted text-xs">Thesis: </span><span className="text-tc-sub">{e.thesis}</span></div>}
                    {e.violations?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {e.violations.map((v) => <span key={v.code} className="text-[11px] text-tc-pass bg-tc-pass/10 rounded px-2 py-0.5">{v.label}</span>)}
                      </div>
                    )}
                    {Object.entries(e.answers || {}).map(([k, val]) => val && (
                      <div key={k} className="text-[13px]"><span className="text-tc-muted capitalize">{k.replace(/_/g, " ")}: </span><span className="text-tc-sub">{val}</span></div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
function KV({ k, v }) { return (<div className="rounded bg-white/5 px-2 py-1.5"><span className="text-[9px] uppercase text-tc-muted block">{k}</span><span>{v ?? "—"}</span></div>); }
